import { Box, IconButton, Button, Typography, Paper, TextField } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import AnswerOptionRichEditor from "./AnswerOptionRichEditor";

// "Shown at" is the position this pair's match takes in the student's dropdown pool. Papers print their
// answer column deliberately scrambled; leaving it blank falls back to a hash shuffle, which can land on
// the row order and let a student score full marks by picking 1, 2, 3, 4 straight down.
const MatchingEditor = ({ options, setOptions }) => {
  const handleChange = (index, field, value) => {
    const updated = [...options];
    updated[index] = { ...updated[index], [field]: value };
    setOptions(updated);
  };

  const addPair = () => {
    if (options.length < 10) {
      setOptions([...options, { text: "", match_text: "" }]);
    }
  };

  // A distractor is an answer-side item with no left item: the paper prints five expressions for three
  // rows, and without these the student's dropdown holds only right answers. It joins the pool and is
  // never a correct pairing, so it is stored as an option whose left text is empty.
  const addDistractor = () => {
    if (options.length < 10) {
      setOptions([...options, { text: "", match_text: "", is_distractor: true }]);
    }
  };
  // `is_distractor` is a UI-only marker on rows added here. Rows loaded from the API don't carry it,
  // so a stored row with a right item and no left one is read back as the distractor it is — while a
  // pair row the author has only just added (both sides still blank) stays a pair.
  const isDistractor = (opt) =>
    opt.is_distractor === true
    || (opt.is_distractor === undefined
      && String(opt.text || "").replace(/<[^>]*>/g, "").trim() === ""
      && String(opt.match_text || "").replace(/<[^>]*>/g, "").trim() !== "");
  const pairCount = options.filter((o) => !isDistractor(o)).length;

  const positions = options.map((o) => (o.match_order == null || o.match_order === "" ? "" : String(o.match_order)));
  const duplicatePosition = (index) =>
    positions[index] !== "" && positions.some((p, i) => i !== index && p === positions[index]);
  const allPositionsSet = positions.every((p) => p !== "") && new Set(positions).size === positions.length;
  // With distractors in the pool "straight down the list" is no longer the answer, so the warning
  // below only applies when every pool item belongs to a row.
  const identityOrder = allPositionsSet && pairCount === options.length
    && positions.every((p, i) => Number(p) === i + 1);

  const setPosition = (index, raw) =>
    handleChange(index, "match_order", String(raw).replace(/[^0-9]/g, "").slice(0, 2));

  const poolPreview = allPositionsSet
    ? options
        .map((o, i) => ({ text: o.match_text, pos: Number(positions[i]) }))
        .sort((a, b) => a.pos - b.pos)
        .map((p) => String(p.text || "").replace(/<[^>]*>/g, "").trim())
    : [];

  const removePair = (index) => {
    if (!isDistractor(options[index]) && pairCount <= 2) return;
    setOptions(options.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: "#444" }}>
        Matching Pairs (left item → right match)
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: "#666" }}>
        "Shown at" sets where each match appears in the student's dropdown — set it to the order printed on
        the paper so the answers stay mixed up. Leave every box blank to shuffle automatically.
        Use "Add Extra Answer" when the paper's answer column holds more items than there are rows to
        match — they join the dropdown but are never a correct choice.
      </Typography>
      {options.map((opt, index) => {
        const distractor = isDistractor(opt);
        return (
        <Paper
          key={index}
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mb: 1.5,
            p: 1.5,
            border: "1px solid",
            borderColor: distractor ? "#f0c98a" : "#e0e0e0",
            borderRadius: 1,
            background: distractor ? "#fffaf1" : "#fafafa",
          }}
        >
          <Box sx={{
            minWidth: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: distractor ? "#d97706" : "#6366f1",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 600,
          }}>
            {index + 1}
          </Box>
          {distractor ? (
            <Box sx={{ flex: 1, minWidth: 0, alignSelf: "center" }}>
              <Typography variant="caption" sx={{ color: "#b45309", fontWeight: 600 }}>
                Extra answer — no item matches it
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <AnswerOptionRichEditor
                value={opt.text}
                onChange={(html) => handleChange(index, "text", html)}
                placeholder={`Item ${index + 1}`}
              />
            </Box>
          )}
          <Typography sx={{ mx: 1, color: distractor ? "#d97706" : "#6366f1", fontWeight: 600, flexShrink: 0 }}>→</Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <AnswerOptionRichEditor
              value={opt.match_text}
              onChange={(html) => handleChange(index, "match_text", html)}
              placeholder={distractor ? "Extra answer" : `Match ${index + 1}`}
            />
          </Box>
          <TextField
            size="small"
            label="Shown at"
            value={positions[index]}
            onChange={(e) => setPosition(index, e.target.value)}
            error={duplicatePosition(index)}
            inputProps={{ inputMode: "numeric", style: { textAlign: "center" } }}
            sx={{ width: 84, flexShrink: 0 }}
          />
          <IconButton
            size="small"
            color="error"
            onClick={() => removePair(index)}
            disabled={!distractor && pairCount <= 2}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Paper>
        );
      })}
      <Button
        size="small"
        startIcon={<Add />}
        onClick={addPair}
        disabled={options.length >= 10}
        sx={{
          color: "#6366f1",
          "&:hover": { background: "rgba(99, 102, 241, 0.08)" }
        }}
      >
        Add Pair
      </Button>
      <Button
        size="small"
        startIcon={<Add />}
        onClick={addDistractor}
        disabled={options.length >= 10}
        sx={{ ml: 1, color: "#d97706", "&:hover": { background: "rgba(217, 119, 6, 0.08)" } }}
      >
        Add Extra Answer
      </Button>
      {poolPreview.length > 0 && (
        <Box sx={{ mt: 1, p: 1.5, border: "1px solid #e0e0e0", borderRadius: 1, bgcolor: "#fafafa" }}>
          <Typography variant="caption" sx={{ display: "block", fontWeight: 600, color: "#444" }}>
            Student dropdown order
          </Typography>
          <Typography variant="caption" sx={{ color: "#666" }}>
            {poolPreview.map((t, i) => `${i + 1}. ${t}`).join("   ")}
          </Typography>
          {identityOrder && (
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#d32f2f" }}>
              This is the same order as the rows above — a student can score full marks by picking 1, 2, 3, 4
              straight down. Reorder it to match the paper.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MatchingEditor;
